import React from "react";
import { Image, Text, View } from "@react-pdf/renderer";
import {
  convertImageUrlForPDF,
  normalizeRiskLevel,
  pdfStyles,
} from "@repo/print-pdf/shared";
import { PDFBevaEstimateBlock } from "../../print/components/PDFBevaEstimateBlock";
import type { PrintDataFields } from "../../print/hooks/use-print-data";

/** Valeur Beva affichée avec son unité ; « — » quand elle n'est pas disponible. */
const formatBeva = (beva: PrintDataFields["beva"]): string => {
  if (beva === "—" || !beva) return "—";
  return `${beva} €`;
};

/** URL absolue de l'image de l'arbre (react-pdf ne charge pas les chemins relatifs). */
const getCompleteTreeImageUrl = (
  houppierLevel: string,
  troncLevel: string,
  colletLevel: string,
): string => {
  const imagePath = `/images/Tree/complete/${houppierLevel}_${troncLevel}_${colletLevel}_Complete.png`;
  if (typeof window !== "undefined") {
    return `${window.location.protocol}//${window.location.host}${imagePath}`;
  }
  return imagePath;
};

/** Contenu du « Diagnostic visuel » : estimation Beva, légende de risque, silhouette et photos. */
export const DiagnosticVisuelBlock: React.FC<{ data: PrintDataFields }> = ({
  data,
}) => (
  <>
    <View style={{ marginTop: 4, marginBottom: 6, width: "100%" }}>
      <PDFBevaEstimateBlock
        formattedBeva={formatBeva(data.beva)}
        bevaDetail={data.bevaDetail}
      />
    </View>
    <View
      style={{
        paddingVertical: 10,
        display: "flex",
        flexDirection: "row",
        gap: 10,
        alignItems: "flex-start",
        justifyContent: "space-between",
      }}
    >
      <View>
        <View style={pdfStyles.riskEvaluation}>
          <Text style={pdfStyles.riskEvalTitle}>Évaluation du risque</Text>
          <View style={pdfStyles.riskBar}>
            <View style={pdfStyles.riskBarItemContainer}>
              <View style={[pdfStyles.riskBarItem, pdfStyles.riskLow]} />
              <Text style={pdfStyles.riskLabel}> Risque faible</Text>
            </View>
            <View style={pdfStyles.riskBarItemContainer}>
              <View style={[pdfStyles.riskBarItem, pdfStyles.riskModerate]} />
              <Text style={pdfStyles.riskLabel}> Risque modéré</Text>
            </View>
            <View style={pdfStyles.riskBarItemContainer}>
              <View style={[pdfStyles.riskBarItem, pdfStyles.riskHigh]} />
              <Text style={pdfStyles.riskLabel}> Risque élevé</Text>
            </View>
            <View style={pdfStyles.riskBarItemContainer}>
              <View style={[pdfStyles.riskBarItem, pdfStyles.riskImminent]} />
              <Text style={pdfStyles.riskLabel}> Risque imminent</Text>
            </View>
          </View>
        </View>
      </View>
    </View>

    {/* Photos with Tree Risk Visualization - 4 columns */}
    <View style={[pdfStyles.grid4, { marginTop: 5 }]}>
      {/* Tree Risk Visualization Column */}
      <View style={pdfStyles.columnNoLeft}>
        <View style={pdfStyles.photoContainer}>
          <Image
            src={getCompleteTreeImageUrl(
              normalizeRiskLevel(data.risque.houppier),
              normalizeRiskLevel(data.risque.tronc),
              normalizeRiskLevel(data.risque.collet),
            )}
            style={pdfStyles.photo}
          />
        </View>
      </View>
      {data.img1 && (
        <View style={pdfStyles.columnNoLeft}>
          <View style={pdfStyles.photoContainer}>
            <Image
              src={convertImageUrlForPDF(data.img1, { width: 700 })}
              style={pdfStyles.photo}
            />
          </View>
        </View>
      )}
      {data.img2 && (
        <View style={pdfStyles.columnNoLeft}>
          <View style={pdfStyles.photoContainer}>
            <Image
              src={convertImageUrlForPDF(data.img2, { width: 700 })}
              style={pdfStyles.photo}
            />
          </View>
        </View>
      )}
      {data.img3 && (
        <View style={pdfStyles.columnNoLeft}>
          <View style={pdfStyles.photoContainer}>
            <Image
              src={convertImageUrlForPDF(data.img3, { width: 700 })}
              style={pdfStyles.photo}
            />
          </View>
        </View>
      )}
    </View>
  </>
);
